/*
* AudioFileList.js (Please do not remove this comment)
* This file is part of the Audio Management System.
* This component is a list of audio files that are available for selection.
* The component uses the Material-UI library for styling and functionality.
* Props Definition:
* There are two properties (arguments) to be passed to this component:
* 1. audioFiles (Array): A simple array of audio filenames.
* 2. onFileSelect: (file: { name: string, id: string }) => void – Callback function triggered when a user selects a file to play.
*
* Displays a list of audio files for a selected date, allowing users to select a file for playback or download it.
* 
* User Interactions:
* 
* Displays a list of files.
* Each list item shows the file name, and a download icon.
* Clicking the download icon directly triggers the file download using the generated URL.
*
* my version:
* @mui/material: 7.1.0
* @mui/x-date-pickers: 8.3.0
* date-fns: 4.1.0
* wavesurfer.js: 7.9.5
*/

import React, { useEffect, useRef } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Box,
  Paper,
  Typography,
  Divider,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  CloudDownload as DownloadIcon,
  PlayArrow as PlayIcon,
  MusicNote as MusicIcon,
} from '@mui/icons-material';
import { useAuthContext } from '../hooks/useAuthContext';

// Accept sx prop and merge it
const AudioFileList = ({ audioFiles = [], onFileSelect, basePath = '', selectedFile, sx: propSx }) => {
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = React.useState(null);
  const listRef = useRef(null);
  const lastScrollTop = useRef(0);
  const { user } = useAuthContext();

  // 點擊時記錄 scrollTop
  const handleFileSelect = (fileName) => {
    const list = listRef.current;
    if (list) lastScrollTop.current = list.scrollTop;
    if (onFileSelect) {
      onFileSelect({
        id: fileName,
        name: fileName,
        audioUrl: `${basePath}/${fileName}`,
        downloadUrl: `${basePath}/${fileName}`
      });
    }
  };

  // 每次 selectedFile 變動後還原 scrollTop
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = lastScrollTop.current;
  }, [selectedFile]);

  // 處理文件下載
  const handleDownload = async (e, fileName) => {
    e.stopPropagation(); // 防止觸發文件選擇事件
    
    if (!user || !user.token) { // 檢查 user 和 user.token
      console.error('Download failed: User token is not available.');
      alert('Authentication token is missing. Cannot download file.');
      return;
    }

    setIsDownloading(fileName);
    
    try {
      const downloadUrl = `${basePath}/${fileName}`;
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`, // 使用 context 中的 user.token
        },
      });

      if (!response.ok) {
        let errorData = 'Failed to download file.';
        try {
          const errorJson = await response.json();
          errorData = errorJson.message || errorJson.error || JSON.stringify(errorJson);
        } catch (parseError) {
          errorData = `HTTP error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorData);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      
      // 從 URL 解析日期和時間資訊
      // downloadUrl 格式: /api/v1/device/audio/E4618C41172C272/2025-07-30/03-00-40.wav
      const urlParts = downloadUrl.split('/');
      const dateStr = urlParts[urlParts.length - 2]; // 2025-07-30
      const timeFileName = urlParts[urlParts.length - 1]; // 03-00-40.wav
      
      // 解析時間部分 (移除副檔名)
      const timeMatch = timeFileName.match(/(\d+)-(\d+)-(\d+)\.(\w+)$/);
      
      let actualFileName = fileName; // 預設使用原始檔名
      
      if (dateStr && timeMatch) {
        const [_, hours, minutes, seconds, extension] = timeMatch;
        
        // 建立 UTC 時間
        const utcDateTimeString = `${dateStr}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}Z`;
        const utcDate = new Date(utcDateTimeString);
        
        // 檢查日期是否有效
        if (!isNaN(utcDate.getTime())) {
          // 轉換為本地時間並格式化檔名
          const localYear = utcDate.getFullYear();
          const localMonth = String(utcDate.getMonth() + 1).padStart(2, '0');
          const localDay = String(utcDate.getDate()).padStart(2, '0');
          const localHours = String(utcDate.getHours()).padStart(2, '0');
          const localMinutes = String(utcDate.getMinutes()).padStart(2, '0');
          const localSeconds = String(utcDate.getSeconds()).padStart(2, '0');
          
          // 組合成新的檔名: YYYY-MM-DD_HH-MM-SS.extension
          actualFileName = `${localYear}-${localMonth}-${localDay}_${localHours}-${localMinutes}-${localSeconds}.${extension}`;
          
          console.log('UTC DateTime:', utcDateTimeString);
          console.log('Local DateTime filename:', actualFileName);
        } else {
          console.warn('Invalid date parsed from URL, using original filename');
        }
      } else {
        console.warn('Could not parse date/time from URL, using original filename');
        
        // 如果 URL 解析失敗，嘗試從 Content-Disposition header 獲取檔名
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (fileNameMatch && fileNameMatch.length === 2) {
            actualFileName = fileNameMatch[1];
          }
        }
      }

      // 清理檔名 (保持原有的清理邏輯)
      actualFileName = actualFileName.trim();
      
      console.log('downloadUrl:', downloadUrl);
      console.log('Filename before sanitize:', `"${actualFileName}"`);

      // 更積極的清理 - 但保留更多有用字元 (包括底線)
      let sanitizedFileName = actualFileName.replace(/[^a-zA-Z0-9._-]/g, '_');
      
      // 合併連續的底線
      sanitizedFileName = sanitizedFileName.replace(/__+/g, '_');

      // 移除頭尾底線
      sanitizedFileName = sanitizedFileName.replace(/^_+|_+$/g, '');

      // 確保檔名不為空
      if (!sanitizedFileName) {
        sanitizedFileName = "downloaded_audio_file" + (actualFileName.includes('.') ? actualFileName.substring(actualFileName.lastIndexOf('.')) : '.wav');
      }
      
      console.log('Downloading file (final sanitized name):', `"${sanitizedFileName}"`);
      a.download = sanitizedFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

    } catch (error) {
      console.error('Failed to download file:', error);
      alert(`Download failed: ${error.message}`);
    } finally {
      setIsDownloading(null);
    }
  };

  // 從文件名提取時間 (假設格式為例如 "00_30_15D65.wav")
  const extractTimeFromFilename = (filename) => {
    try {
      // 匹配時間格式 HH_MM_SS
      // const timeMatch = filename.match(/(\d+)_(\d+)_(\d+)/);
      const timeMatch = filename.match(/(\d+)-(\d+)-(\d+)/);
      
      if (timeMatch) {
        const [_, hours, minutes, seconds] = timeMatch;
        
        // 建立一個 UTC 時間，然後讓瀏覽器自動轉換為本地時間
        const utcTimeString = `1970-01-01T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}Z`;
        const utcDate = new Date(utcTimeString);
        
        // 格式化為本地時間
        return utcDate.toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
      }
      
      return '';
    } catch (error) {
      console.error('Error extracting time from filename:', error);
      return '';
    }
  };

  // 如果沒有文件
  if (!audioFiles || audioFiles.length === 0) {
    // Apply propSx to the placeholder as well if it should fill the space
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          flexDirection: 'column',
          ...propSx // Merge sx from parent
        }}
      >
        <MusicIcon color="disabled" sx={{ fontSize: 48, mb: 2 }} />
        <Typography variant="body1" color="text.secondary">
          No available audio files
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...propSx // Merge sx from parent (e.g., flexGrow: 1, minHeight: 0)
      }}
    >
      <Box // Header
        p={1.5} // Adjusted padding
        bgcolor={theme.palette.primary.main}
        color={theme.palette.primary.contrastText}
        display="flex"
        alignItems="center"
        sx={{ flexShrink: 0 }} // Header should not grow or shrink
      >
        <MusicIcon sx={{ mr: 1 }} />
        <Typography variant="h6">Audio Files</Typography>
      </Box>
      
      <Divider sx={{ flexShrink: 0 }} />
      
      <List
        ref={listRef}
        sx={{ 
          flexGrow: 1, // List takes up remaining space in the Paper
          overflowY: 'auto', 
          p: 0,
          minHeight: 0, // Important for scrollable flex children
        }}
      >
        {audioFiles.map((fileName) => {
          const isSelected = selectedFile && selectedFile.name === fileName;
          const timeDisplay = extractTimeFromFilename(fileName);

          return (
            <ListItem
              key={fileName}
              button
              onClick={() => handleFileSelect(fileName)}
              selected={isSelected}
              divider
              sx={{
                backgroundColor: isSelected ? alpha(theme.palette.primary.light, 0.2) : 'transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.action.hover, 0.7),
                },
                py: 0.75, // Adjusted padding
                px: 1.5,  // Adjusted padding
              }}
            >
              <MusicIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} />
              <ListItemText 
                primary={fileName} 
                secondary={timeDisplay || 'No time info'}
                primaryTypographyProps={{ 
                  variant: 'body2', 
                  fontWeight: isSelected ? 'fontWeightMedium' : 'fontWeightRegular',
                  color: isSelected ? 'primary.main' : 'text.primary',
                  sx: { wordBreak: 'break-all' }
                }}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
              <ListItemSecondaryAction>
                <Tooltip title="Download audio file">
                  <IconButton 
                    edge="end" 
                    aria-label="download" 
                    onClick={(e) => handleDownload(e, fileName)}
                    disabled={isDownloading === fileName}
                  >
                    {isDownloading === fileName ? <CircularProgress size={24} /> : <DownloadIcon />}
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
      
      <Box // Footer
        px={1.5} // Adjusted padding
        py={0.75} // Adjusted padding
        bgcolor={theme.palette.grey[100]} 
        display="flex" 
        justifyContent="space-between"
        alignItems="center"
        sx={{ flexShrink: 0, borderTop: `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="body2" color="text.secondary">
          Total {audioFiles.length} Files
        </Typography>
      </Box>
    </Paper>
  );
};

export default AudioFileList;